import { Drawer, Box, Toolbar } from '@mui/material';
import React from 'react';
import { usePermissions } from '../../hooks/permissions/usePermissions';

import { UserAvatar } from '../ui/UserAvatar';
import { CompanyLogo } from './CompanyLogo';
import { NavigationMenu } from './NavigationMenu';
import { VersionInfo } from './VersionInfo';

interface LayoutDrawerProps {
  user: any;
  isMobile: boolean;
  drawerOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  adminSectionOpen: boolean;
  superadminSectionOpen: boolean;
  onAdminSectionToggle: () => void;
  onSuperadminSectionToggle: () => void;
  versionInfo: { api: string; web: string };
}

export const LayoutDrawer: React.FC<LayoutDrawerProps> = React.memo(({
  user,
  isMobile,
  drawerOpen,
  onClose,
  currentPath,
  onNavigate,
  onLogout,
  adminSectionOpen,
  superadminSectionOpen,
  onAdminSectionToggle,
  onSuperadminSectionToggle,
  versionInfo
}) => {
  const { can } = usePermissions();
  const permissionLabel = (
    can.configureChatModels || can.configureEmbedding || can.configureTextExtraction || can.configureSMTP
      ? 'Administrator'
      : (can.manageUsers || can.manageGroups ? 'Moderator' : 'Standard User')
  );

  const drawerContent = (
    <Box sx={{
      width: 250,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflowX: 'hidden',
      overflowY: 'auto'
    }}>
      <UserAvatar user={user} mode="profile" permissionLabel={permissionLabel} />
      <CompanyLogo />
      <NavigationMenu
        user={user}
        currentPath={currentPath}
        onNavigate={onNavigate}
        onLogout={onLogout}
        adminSectionOpen={adminSectionOpen}
        superadminSectionOpen={superadminSectionOpen}
        onAdminSectionToggle={onAdminSectionToggle}
        onSuperadminSectionToggle={onSuperadminSectionToggle}
      />
      <VersionInfo versionInfo={versionInfo} />
    </Box>
  );

  return isMobile ? (
    <Drawer
      open={drawerOpen}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          // Hardware acceleration for smooth mobile animations
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
          // Prevent horizontal scroll conflicts on mobile
          touchAction: 'pan-y',
          // Fast transitions for better mobile performance
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
        },
      }}
    >
      <Toolbar /> {/* AppBar spacer for proper mobile layout */}
      {drawerContent}
    </Drawer>
  ) : (
    <Drawer
      variant="permanent"
      sx={{
        width: 250,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: 250,
          boxSizing: 'border-box',
          overflowX: 'hidden'
        },
      }}
    >
      <Toolbar /> {/* This creates space under the AppBar */}
      {drawerContent}
    </Drawer>
  );
});
